// @ts-check
import React, { useEffect, useState } from 'react'
import { Select } from '../../../ui'
import { useStore } from '../../../StateProvider'
import { equals, map, isNil, isEmpty } from 'ramda'
import { useTranslation } from 'react-i18next'
import { getOptions, getUserValue } from '../utils'
import { usePrevious } from '../../utils/hooks'

/**
 * @param {{
 * label: string,
 * name: string,
 * additionalInfo?: string,
 * dependOnBooleanFldName: string,
 * required: boolean,
 * order: number }} props
 */
const ExternalTransactionsFrequency = ({
  label,
  name,
  additionalInfo,
  dependOnBooleanFldName
}) => {
  const { t } = useTranslation()
  const [value, setValue] = useState('')
  const [list, setList] = useState([])
  const [isValid, setIsValid] = useState(null)
  const [shouldPresetValue, setShouldPresetValue] = useState(true)
  const [{
    data: { transactionFrequencies },
    form,
    form: { accId },
    user
  }, dispatch] = useStore()
  const [isVisible, setIsVisible] = useState(!dependOnBooleanFldName)
  const dependOnBooleanFldValue = form[accId][dependOnBooleanFldName]
  const prevDependOnFieldValue = usePrevious(dependOnBooleanFldValue)
  const userValue = getUserValue(accId, user, name)
  const prevUserValue = usePrevious(userValue)

  useEffect(() => {
    if (transactionFrequencies && transactionFrequencies[name]?.length) {
      setList(getOptions(transactionFrequencies[name]))
    }
  }, [transactionFrequencies, name])

  useEffect(() => {
    if (
      !dependOnBooleanFldName ||
      (prevDependOnFieldValue === dependOnBooleanFldValue && prevUserValue === userValue)
    ) return

    if (dependOnBooleanFldValue === 'Y') {
      if (userValue && shouldPresetValue) {
        presetValue(userValue)
        setIsVisible(true)
      } else {
        setIsVisible(true)
        dispatch({ type: 'addValidationFld', name })
        if (!isNil(value) && !isEmpty(value)) {
          presetValue(value)
        }
      }
    } else {
      dispatch({ type: 'removeValidationFld', name })
      dispatch({ type: 'removeFieldValue', name })
      setIsVisible(false)
    }
  }, [dependOnBooleanFldValue, userValue, shouldPresetValue, value, name])

  /**
   * @param {string} value
   */
  const getSelectValue = (value) => (equals(value, t('generatedField.choose')) ? '' : value)

  const presetValue = (value) => {
    const stringValue = value.toString()
    setValue(stringValue)
    const isValid = stringValue.length > 0
    setIsValid(isValid)
    dispatch({ type: 'setExtFieldValue', name, value })
    dispatch({ type: 'setValidationFld', name, value: isValid })
  }
  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const onChange = ({ target: { value, name } }) => {
    const selectedValue = getSelectValue(value)
    setValue(selectedValue)
    setIsValid(selectedValue.length > 0)
    dispatch({ type: 'setExtFieldValue', name, value: +selectedValue })
    dispatch({ type: 'setValidationFld', name, value: !!selectedValue.length })
    setShouldPresetValue(false)
  }

  return isVisible ? (
    <Select
      value={value || ''}
      label={t(label)}
      placeholder={t('generatedField.choose')}
      options={list}
      onChange={onChange}
      name={name}
      key={name}
      error=''
      valid={isValid}
      info={additionalInfo ? t(`generatedField.${name}AdditionalInfo`) : ''}
    />
  ) : null
}

const getExternalTransactionsFrequencyFields = map(currencyField => {
  const { i18n, name, required, order, dependOnBooleanFldName, additionalInfo } = currencyField
  return (
    <ExternalTransactionsFrequency
      label={i18n ? `generatedField.${i18n}` : ''}
      name={name}
      required={required}
      key={name}
      order={order}
      dependOnBooleanFldName={dependOnBooleanFldName}
      additionalInfo={additionalInfo}
    />
  )
})

export { ExternalTransactionsFrequency, getExternalTransactionsFrequencyFields }
